import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginStart, loginSuccess, loginFailure } from "../features/authSlice";
import ApiCustom from "../utils/ApiCustom";
import { toast } from "react-toastify";
import { EyeSlashIcon, EyeIcon } from "@heroicons/react/24/solid";
import ForgotPasswordForm from "../components/ForgotPasswordForm";
import ResetPasswordForm from "../components/ResetPasswordForm";
import GoogleLoginButton from "../components/client/GoogleLoginButton";
import { Link } from "react-router-dom";
import { HomeIcon } from "@heroicons/react/24/outline";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const { error } = useSelector((state) => state.auth);
  const [emailForResetPassword, setEmailForResetPassword] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPasswordForm, setShowResetPasswordForm] = useState(false);
  const dispatch = useDispatch();
  const hasCalled = useRef(false);

  useEffect(() => {
    if (!hasCalled.current) {
      if (error) {
        toast.error(error);
        dispatch(loginFailure(null));
      }
      hasCalled.current = true;
    }
  }, [dispatch, error]);

  const handleLogin = async (e) => {
    e.preventDefault();
    dispatch(loginStart());
    try {
      const response = await ApiCustom.post(
        "/login",
        {
          username,
          password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      localStorage.setItem("access_token_bccu", response.data?.data?.token);
      localStorage.setItem("full_name", response.data?.data?.userFullName);
      localStorage.setItem("type_user", response.data?.data?.type);
      localStorage.setItem("userId", response.data?.data?.id);
      localStorage.setItem("userEmail", response.data?.data?.userEmail);
      localStorage.setItem("userPhone", response.data?.data?.userPhone);
      // localStorage.setItem("companyId", response.data?.data?.companyId);
      localStorage.setItem(
        "companyId",
        response.data?.data?.companies?.find(
          (e) => e?.CompaniesHasUsers?.isUserRepresentOfCompany
        )?.id || null
      );
      localStorage.setItem(
        "permissions",
        JSON.stringify(response.data?.data?.role?.permissions || [])
      );
      if (response.data?.data?.type === "admin")
        window.location.href = "/admin/dashboard";
      else window.location.href = "/";
      dispatch(loginSuccess(response.data));
    } catch (error) {
      dispatch(loginFailure(error.response?.data?.message || "Login failed"));
      setUsername("");
      setPassword("");
      toast.error(
        error.response?.data?.messages || "Sai tên đăng nhập hoặc mật khẩu!"
      );
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="container mx-auto">
      <nav className="flex text-gray-500 text-sm mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2">
          <li className="inline-flex items-center">
            <Link to="/" className="inline-flex items-center hover:text-blue-600 font-medium">
              <HomeIcon className="w-4 h-4 mr-2 text-gray-400" />
              Trang chủ
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-3 h-3 mx-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M7.05 4.05a.75.75 0 011.06 0l5.25 5.25a.75.75 0 010 1.06l-5.25 5.25a.75.75 0 11-1.06-1.06L11.19 10 7.05 5.91a.75.75 0 010-1.06z" />
              </svg>
              <span className="ml-1 text-gray-400 font-medium">Đăng nhập</span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="flex justify-center items-center">
        {showForgotPassword ? (
          <ForgotPasswordForm
            onBack={() => setShowForgotPassword(false)}
            onOtpSent={(email) => {
              setEmailForResetPassword(email);
              setShowForgotPassword(false);
              setShowResetPasswordForm(true);
            }}
          />
        ) : showResetPasswordForm ? (
          <ResetPasswordForm
            onBack={() => setShowResetPasswordForm(false)}
            email={emailForResetPassword}
            onChangeEmail={() => {
              setShowResetPasswordForm(false);
              setShowForgotPassword(true);
              setEmailForResetPassword(null);
            }}
          />
        ) : (
          <form
            className="bg-base-100 shadow-lg rounded-lg p-6 w-80"
            onSubmit={handleLogin}
          >
            <h2 className="text-2xl font-bold text-center mb-4">Đăng nhập</h2>
            <input
              type="text"
              placeholder="Tên đăng nhập"
              value={username}
              required
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 border rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="relative mb-4">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Mật khẩu"
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 focus:outline-none"
              >
                {showPassword ? (
                  <span className="text-sm">
                    <EyeIcon className="h-6 w-6 text-gray-500" />
                  </span>
                ) : (
                  <span className="text-sm">
                    <EyeSlashIcon className="h-6 w-6 text-gray-500" />
                  </span>
                )}
              </button>
            </div>
            <div className="flex mb-2 justify-end">
              <span
                className="text-blue-500 cursor-pointer inline-block"
                onClick={() =>
                  emailForResetPassword == null
                    ? setShowForgotPassword(true)
                    : setShowResetPasswordForm(true)
                }
              >
                Quên mật khẩu
              </span>
            </div>
            <div className="mb-2 text-[14px]">
              <span className="text-red-500">* </span>Mọi thông tin vui lòng liên
              hệ Ban điều hành BCCU - 0947703456
            </div>
            <button
              type="submit"
              className="w-full btn btn-primary py-2 rounded-md transition duration-300 btn-sm"
            >
              Đăng nhập
            </button>
            <GoogleLoginButton />
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
